/**
 * Lightweight client-side sound manager.
 *
 * - Uses the Web Audio API to synthesize short tones (no audio assets shipped).
 * - Volume (0..1) and mute are persisted in localStorage and shared by mock and
 *   live mode through the single exported singleton.
 * - The AudioContext is created lazily on the first user gesture, so we never hit
 *   browser autoplay restrictions.
 *
 * This module is framework-agnostic; `useSound` (a thin React hook) lives at the
 * bottom for component use.
 */

import { useSyncExternalStore } from 'react';

export type SoundEvent =
  | 'select'
  | 'submit'
  | 'reveal'
  | 'winner'
  | 'countdown'
  | 'error';

export interface SoundSettings {
  volume: number; // 0..1
  muted: boolean;
}

const VOLUME_KEY = 'dad_sound_volume';
const MUTED_KEY = 'dad_sound_muted';

function clampVolume(value: number): number {
  if (Number.isNaN(value)) return 0.6;
  return Math.min(1, Math.max(0, value));
}

function readSettings(): SoundSettings {
  if (typeof localStorage === 'undefined') return { volume: 0.6, muted: false };
  const rawVolume = localStorage.getItem(VOLUME_KEY);
  const rawMuted = localStorage.getItem(MUTED_KEY);
  return {
    volume: rawVolume === null ? 0.6 : clampVolume(Number(rawVolume)),
    muted: rawMuted === 'true',
  };
}

/** A simple tone recipe per game event. Kept short and gentle on purpose. */
interface ToneStep {
  freq: number;
  start: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
}

const RECIPES: Record<SoundEvent, ToneStep[]> = {
  select: [{ freq: 523.25, start: 0, duration: 0.08, type: 'triangle' }],
  submit: [
    { freq: 587.33, start: 0, duration: 0.09, type: 'triangle' },
    { freq: 880, start: 0.08, duration: 0.12, type: 'triangle' },
  ],
  reveal: [{ freq: 392, start: 0, duration: 0.12, type: 'sine' }],
  winner: [
    { freq: 523.25, start: 0, duration: 0.12, type: 'square', gain: 0.6 },
    { freq: 659.25, start: 0.11, duration: 0.12, type: 'square', gain: 0.6 },
    { freq: 783.99, start: 0.22, duration: 0.22, type: 'square', gain: 0.6 },
  ],
  countdown: [{ freq: 440, start: 0, duration: 0.07, type: 'sine' }],
  error: [
    { freq: 196, start: 0, duration: 0.18, type: 'sawtooth', gain: 0.5 },
    { freq: 146.83, start: 0.12, duration: 0.2, type: 'sawtooth', gain: 0.5 },
  ],
};

class SoundManager {
  private settings: SoundSettings = readSettings();

  private listeners = new Set<() => void>();

  private ctx: AudioContext | null = null;

  getSettings(): SoundSettings {
    return this.settings;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private emit(): void {
    for (const l of this.listeners) l();
  }

  setVolume(volume: number): void {
    this.settings = { ...this.settings, volume: clampVolume(volume) };
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(VOLUME_KEY, String(this.settings.volume));
    }
    this.emit();
  }

  setMuted(muted: boolean): void {
    this.settings = { ...this.settings, muted };
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(MUTED_KEY, String(muted));
    }
    this.emit();
  }

  toggleMuted(): void {
    this.setMuted(!this.settings.muted);
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    if (!this.ctx) {
      try {
        this.ctx = new Ctor();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  play(event: SoundEvent): void {
    const { volume, muted } = this.settings;
    if (muted || volume <= 0) return;
    const ctx = this.ensureContext();
    if (!ctx) return;

    const recipe = RECIPES[event];
    const now = ctx.currentTime;
    for (const step of recipe) {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.type = step.type ?? 'sine';
      osc.frequency.value = step.freq;
      const peak = volume * (step.gain ?? 0.4);
      const startAt = now + step.start;
      const endAt = startAt + step.duration;
      gainNode.gain.setValueAtTime(0.0001, startAt);
      gainNode.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), startAt + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);
      osc.connect(gainNode).connect(ctx.destination);
      osc.start(startAt);
      osc.stop(endAt + 0.02);
    }
  }
}

export const soundManager = new SoundManager();

/** Play a one-off sound (safe no-op if muted / unsupported). */
export function playSound(event: SoundEvent): void {
  soundManager.play(event);
}

/** React hook exposing live sound settings + controls. */
export function useSound() {
  const settings = useSyncExternalStore(
    soundManager.subscribe,
    () => soundManager.getSettings(),
    () => soundManager.getSettings(),
  );
  return {
    volume: settings.volume,
    muted: settings.muted,
    setVolume: (v: number) => soundManager.setVolume(v),
    setMuted: (m: boolean) => soundManager.setMuted(m),
    toggleMuted: () => soundManager.toggleMuted(),
    play: (event: SoundEvent) => soundManager.play(event),
  };
}
